<script setup lang="ts">
import { computed } from 'vue';
import { PhCaretRight, PhFolder } from '@phosphor-icons/vue';
import { useDirectoryTreeContextMenu } from '@/components/document/sub-components/file-manager/logic/core/useDirectoryTreeContextMenu';
import { useFileManagerTreeRow } from '@/components/document/sub-components/file-manager/logic/core/useFileManagerTreeRow';
import { canDragSource } from '@/components/document/sub-components/file-manager/logic/drag-drop/dragGuards';
import { useFileManagerDrag } from '@/components/document/sub-components/file-manager/logic/drag-drop/useFileManagerDrag';
import { useFileManagerPermissions } from '@/components/document/sub-components/file-manager/permissions/useFileManagerPermissions';
import { DropIndicator } from '@/components/document/sub-components/file-manager/sub-components/drop-indicator';
import { DropZone } from '@/components/document/sub-components/file-manager/sub-components/drop-zone';
import { Spinner } from '@/components/document/sub-components/file-manager/sub-components/spinner';
import type { FolderLikeItem } from '@/components/document/sub-components/file-manager/types';
import { DirectoryList } from '@/components/document/sub-components/file-manager/views/directory-list-view/sub-components/directory-list';

const props = defineProps<{
  item: FolderLikeItem;
}>();

const {
  isSelected,
  isExpanded,
  isAncestorOfSelected,
  hasFolderChildren,
  isLoading,
  directories,
  onItemClick,
  onToggleExpand,
} = useFileManagerTreeRow(
  () => props.item.path,
  { knownHasFolderChildren: () => props.item.hasFolderChildren },
);

const { draggedItems, dragOverPath } = useFileManagerDrag();
const { permissionsAt } = useFileManagerPermissions();
const { onContextMenu, renamingPaths, deletingPaths, copyingPaths } = useDirectoryTreeContextMenu(
  () => props.item,
);

// The same guard the grid applies via useFileGridInteractions' canDragItem — shared rather than
// restated, since both surfaces need the identical permission-plus-item-kind pair and a mount row at
// the tree's top level is never draggable at any tier. Capabilities come from this row's own path, so
// a tree spanning several projects gates each subtree by its own permission set.
const canDrag = computed(() =>
  canDragSource(props.item, permissionsAt(props.item.path)),
);
const isDragSource = computed(() =>
  draggedItems.value.some((dragged) => dragged.path === props.item.path),
);

// Bound only on this row's own element, never on `.directory-item-children` below — that
// container holds further draggable rows of its own, and a draggable element that contains other
// draggable descendants is a well-known cross-browser landmine (Chrome renders the whole nested
// region as the drag ghost and its drag session gets confused, breaking dragover/drop elsewhere
// in the tree until the drag ends).
const onDragStart = (event: DragEvent) => {
  draggedItems.value = [props.item];
  // 'copyMove', not 'move': a drop into the other document library is a copy, and which it will be
  // is not known until the drop lands. Each drop zone narrows this to a concrete dropEffect.
  if (event.dataTransfer) event.dataTransfer.effectAllowed = 'copyMove';
};

const onDragEnd = () => {
  draggedItems.value = [];
  dragOverPath.value = null;
};
</script>

<template>
  <!-- Bound on `.directory-item` alone, never spanning `.directory-item-children` below: the drop
       target is exactly the visible list item. The wrapper component that used to own these handlers
       sat above BOTH nodes of this fragment, which made the indent gutter of an expanded subtree
       resolve to the parent row as a drop target and left no element that could host the upload
       indicator without framing the whole subtree.
       No `.stop` modifiers needed: the children container is this row's SIBLING, so a nested row's
       drag events never bubble through an ancestor `.directory-item`, and the sidebar has no
       background upload zone above these rows for them to compete with. -->
  <DropZone
    :path="props.item.path"
    :item="props.item"
    accepts-upload
    v-slot="{ isMoveDragOver, isUploadDragOver, onDragEnter, onDragLeave, onDragOver, onDrop }"
  >
    <div
      class="directory-item"
      :draggable="canDrag"
      :class="{
        'is-selected': isSelected,
        'is-move-drag-over': isMoveDragOver,
        'is-drag-source': isDragSource,
        'is-renaming': renamingPaths.has(item.path),
        'is-deleting': deletingPaths.has(item.path),
        'is-copying': copyingPaths.has(item.path),
      }"
      @click="onItemClick"
      @contextmenu="onContextMenu"
      @dragstart="onDragStart"
      @dragend="onDragEnd"
      @dragenter.prevent="onDragEnter"
      @dragover="onDragOver"
      @dragleave="onDragLeave"
      @drop.prevent="onDrop"
    >
      <button
        v-if="hasFolderChildren"
        type="button"
        class="directory-item-expand"
        :class="{
          'is-expanded': isExpanded,
          'is-ancestor-of-selected': isAncestorOfSelected,
        }"
        @click.stop="onToggleExpand"
      >
        <PhCaretRight :size="14" weight="bold" />
      </button>
      <span v-else class="directory-item-expand-spacer" />
      <PhFolder class="directory-item-icon" :size="20" weight="fill" color="var(--primary-color)" />
      <span class="directory-item-name">
        {{ item.name }}
      </span>
      <DropIndicator v-if="isUploadDragOver" />
    </div>
    <div v-if="hasFolderChildren && isExpanded" class="directory-item-children">
      <Spinner v-if="isLoading" />
      <DirectoryList v-else :items="directories" />
    </div>
  </DropZone>
</template>

<style scoped>
.directory-item {
  display: flex;
  align-items: center;
  width: 100%;
  min-width: 0;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  border-radius: 0.375rem;
  cursor: pointer;
  /* Positioning context for the DropIndicator overlay (native-file drag), which follows this row's
     border-radius via `border-radius: inherit`. Same arrangement as the grid's .details-row. */
  position: relative;
}

.directory-item:hover {
  background-color: #f5f5f5;
}

.directory-item.is-selected {
  background-color: color-mix(in srgb, var(--primary-color) 15%, transparent);
}

/* Internal item move: a soft fill marks the destination folder. A native OS file drag deliberately
   gets no fill — it is marked by the marching-ants DropIndicator instead, so the two drag species
   never look alike. Same split, and the same class name, as the grid's .details-row. */
.directory-item.is-move-drag-over {
  background-color: color-mix(in srgb, var(--primary-color) 10%, transparent);
}

.directory-item.is-drag-source {
  opacity: 0.75;
}

.directory-item.is-renaming,
.directory-item.is-deleting,
.directory-item.is-copying {
  opacity: 0.5;
}

.directory-item-icon {
  flex-shrink: 0;
}

.directory-item-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.directory-item-expand {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  padding: 0.25rem;
  color: #888;
  background: none;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  transition: transform 0.15s ease;
}

.directory-item-expand.is-expanded {
  transform: rotate(90deg);
}

.directory-item-expand.is-ancestor-of-selected {
  background-color: color-mix(in srgb, var(--primary-color) 15%, transparent);
}

.directory-item-expand-spacer {
  flex-shrink: 0;
  width: 1.375rem;
  height: 1.375rem;
}
</style>
